import Card from '../../components/Card';
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api, { DB_TYPE } from '../../api';
import { db } from '../../firebase';
import { getDocs, collection, doc, updateDoc } from 'firebase/firestore';
import Pagination from '../../components/Pagination';
import { useAuth } from '../../context/AuthContext';
import { useT } from '../../utils/useTranslation';
import { motion, AnimatePresence } from 'framer-motion';

const ITEMS_PER_PAGE = 10;

export default function ManageBranchRequests() {
  const { user } = useAuth();
  const t = useT();
  const isRTL = t.language === 'sindhi';

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [page, setPage] = useState(1);

  const load = async () => {
    setLoading(true);
    try {
      if (DB_TYPE === 'firebase') {
        const snap = await getDocs(collection(db, 'branch_requests'));
        let allRequests = snap.docs.map(d => ({ _id: d.id, ...d.data() }));
        
        const role = user?.role || '';
        const isMain = role === 'main_admin';
        const isDistCoord = role === 'district_coordinator';

        if (isMain) {
          // Main Admin should not receive these requests anymore
          setRequests([]);
        } else if (isDistCoord && user?.districtId) {
          // District Coordinator only sees their district's requests
          setRequests(allRequests.filter(r => r.district_id === user.districtId));
        } else {
          // Other admins (Center Body) might still see all
          setRequests(allRequests);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateRequestStatus = async (requestId, nextStatus) => {
    try {
      if (DB_TYPE === 'firebase') {
        await updateDoc(doc(db, 'branch_requests', requestId), { 
          status: nextStatus,
          updatedAt: new Date().toISOString()
        });
      }
      toast.success(isRTL ? 'حيثيت تبديل ڪئي وئي' : `Status updated to ${nextStatus}`);
      load();
    } catch (e) {
      toast.error(isRTL ? 'غلطي ٿي وئي' : 'Update failed');
    }
  };

  const filtered = requests.filter(r => r.status === tab);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32, gap: 24, flexWrap: 'wrap', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <div>
          <h1 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 42 : 32, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 4 }}>
            {isRTL ? 'نئين شاخ لاءِ درخواستون' : 'New Branch Requests'}
          </h1>
          <p style={{ fontSize: 14, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
            {isRTL ? 'نئين شاخن جي قيام لاءِ آيل درخواستن جو انتظام ڪريو' : 'Manage applications for establishing new branches'}
          </p>
        </div>
        <div style={{ fontSize: 14, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
          {filtered.length} {isRTL ? 'درخواستون' : 'requests'}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 24, background: 'var(--sindhi-cream)', padding: 6, borderRadius: 16, width: 'fit-content', direction: isRTL ? 'rtl' : 'ltr' }}>
        {['pending', 'approved', 'rejected'].map(s => (
          <button key={s} onClick={() => { setTab(s); setPage(1); }} style={{ 
            padding: '10px 24px', 
            borderRadius: 12, 
            border: 'none', 
            fontSize: 14, 
            fontWeight: 700, 
            cursor: 'pointer', 
            background: tab === s ? 'white' : 'transparent', 
            color: tab === s ? 'var(--sindhi-navy)' : 'var(--sindhi-text-muted)', 
            boxShadow: tab === s ? '0 4px 12px rgba(0,0,0,0.05)' : 'none',
            fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit',
            transition: 'all 0.2s'
          }}>
            {isRTL ? (s === 'pending' ? 'التوا ۾' : s === 'approved' ? 'منظور ٿيل' : 'مسترد') : s.toUpperCase()}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><div className="spinner" /></div>
      ) : (
        <Card style={{ overflow: 'hidden', border: '1px solid var(--sindhi-cream)', background: 'white' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', borderCollapse: 'collapse', direction: isRTL ? 'rtl' : 'ltr' }}>
              <thead>
                <tr style={{ background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)' }}>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'شاخ جو نالو' : 'Branch Name'}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'شهر/ضلعو' : 'City/District'}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'درخواست گذار' : 'Applicant'}</th>
                  <th style={{ padding: '16px 24px', textAlign: isRTL ? 'right' : 'left', fontSize: 12, fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'عمل' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="popLayout">
                  {filtered.length === 0 ? (
                    <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <td colSpan={4} style={{ padding: 80, textAlign: 'center', color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                        {isRTL ? 'ڪا به درخواست نه ملي.' : 'No requests in this category.'}
                      </td>
                    </motion.tr>
                  ) : filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map(req => (
                    <motion.tr key={req._id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ borderBottom: '1px solid var(--sindhi-cream)' }}>
                      <td style={{ padding: '20px 24px' }}>
                        <div style={{ fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', fontSize: isRTL ? 18 : 15 }}>{isRTL ? (req.branch_name_sindhi || req.branch_name) : req.branch_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--sindhi-text-muted)' }}>{new Date(req.createdAt).toLocaleDateString()}</div>
                      </td>
                      <td style={{ padding: '20px 24px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? (req.city_village_sindhi || req.city_village) : req.city_village}</div>
                        <div style={{ fontSize: 12, color: 'var(--sindhi-maroon)', fontWeight: 800 }}>{req.district_id}</div>
                      </td>
                      <td style={{ padding: '20px 24px' }}>
                        <div style={{ fontWeight: 700, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{req.applicant_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--sindhi-text-muted)' }}>{req.applicant_phone}</div>
                      </td>
                      <td style={{ padding: '20px 24px' }}>
                        <div style={{ display: 'flex', gap: 8, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                          {req.status === 'pending' ? (
                            <>
                              <button className="btn-action" onClick={() => updateRequestStatus(req._id, 'approved')} style={{ background: '#1da352', color: 'white', padding: '8px 16px', borderRadius: 10, border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'منظور ڪريو' : 'Approve'}</button>
                              <button className="btn-action" onClick={() => updateRequestStatus(req._id, 'rejected')} style={{ background: '#feecef', color: '#dc2626', padding: '8px 16px', borderRadius: 10, border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'مسترد' : 'Reject'}</button>
                            </>
                          ) : (
                            <span style={{ padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', background: req.status === 'approved' ? '#e1f9eb' : '#feecef', color: req.status === 'approved' ? '#1da352' : '#dc2626', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? (req.status === 'approved' ? 'منظور ٿيل' : 'مسترد') : req.status}</span>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
          <div style={{ padding: 24 }}>
            <Pagination page={page} totalPages={Math.ceil(filtered.length / ITEMS_PER_PAGE)} onPageChange={setPage} />
          </div>
        </Card>
      )}
    </motion.div>
  );
}